import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  value: number;
  icon: LucideIcon;
  hinweis?: string;
  /** Zahl in Vordergrundfarbe statt grau — es liegt etwas an. */
  betont?: boolean;
  /**
   * Hinweis in Warnfarbe — etwas ist liegengeblieben.
   *
   * Nur der Hinweis, nicht die Zahl: Von fünf fälligen Aufgaben sind zwei
   * überfällig, und eine durchgehend rote Fünf behauptete, alle fünf wären es.
   */
  dringend?: boolean;
  onClick?: () => void;
}

/**
 * Eine Zahl mit Beschriftung.
 *
 * Trägt sowohl „Was ansteht“ als auch den Bestand, deshalb steht sie hier und
 * nicht in einer der beiden Zeilen.
 */
export function Kachel({
  label,
  value,
  icon: Icon,
  hinweis,
  betont,
  dringend,
  onClick,
}: Props) {
  const inhalt = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p
        className={cn(
          'mt-2 text-2xl font-semibold',
          betont ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {value}
      </p>
      {hinweis && (
        <p className={cn('mt-0.5 text-xs', dringend ? 'text-danger' : 'text-muted-foreground')}>
          {hinweis}
        </p>
      )}
    </>
  );

  if (!onClick) return <Card className="p-4">{inhalt}</Card>;

  return (
    <Card
      role="button"
      tabIndex={0}
      className="cursor-pointer p-4 transition-colors hover:bg-muted/40"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {inhalt}
    </Card>
  );
}
